import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * A small anchored menu, on nothing.
 *
 * The repo filter used to be a Radix `Select`, which brings a 36px trigger, a
 * portalled positioner and a scroll-lock for what is a nine-item list in a
 * toolbar. This is the whole behaviour that list needs: click to open, arrows to
 * move, Enter to choose, Escape or a click anywhere else to leave. It is
 * anchored by an absolutely-positioned child rather than a portal, so there is
 * no position math to drift and no second stacking context to reason about.
 *
 * `Menu` is used for choices. Anything destructive stays a visible button with a
 * reason field, never a hidden item in a menu.
 */
export interface MenuOption {
  value: string;
  label: string;
  hint?: string;
}

export function Menu({
  value,
  options,
  onChange,
  label,
  className,
  align = 'start',
}: {
  value: string;
  options: MenuOption[];
  onChange: (value: string) => void;
  /** Accessible name for the trigger; the current option is the visible text. */
  label: string;
  className?: string;
  align?: 'start' | 'end';
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const listId = useId();
  const current = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  // Focus the selected row on open so the arrows have somewhere to start.
  useEffect(() => {
    if (!open) return;
    const active = list.current?.querySelector<HTMLButtonElement>('[data-selected="true"]');
    (active ?? list.current?.querySelector<HTMLButtonElement>('button'))?.focus();
  }, [open]);

  const move = (from: HTMLElement, delta: number) => {
    const items = [...(list.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])];
    const index = items.indexOf(from as HTMLButtonElement);
    items[Math.max(0, Math.min(items.length - 1, index + delta))]?.focus();
  };

  return (
    <div ref={wrap} className={cn('relative', className)}>
      <button
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((previous) => !previous)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'Enter') {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          'border-line-2 text-fg-2 hover:text-foreground hover:border-line-2 hover:bg-raised',
          // Two of these now sit in the Tasks header next to the phone's nav
          // and palette buttons, and a repo can be called
          // `compliance-app-ai-team`: below `sm` the trigger truncates instead
          // of pushing the header wider than the screen.
          'flex h-7 max-w-[8rem] items-center gap-1.5 rounded-md border px-2 text-sm sm:max-w-[13rem]',
          'transition-colors duration-100 outline-none',
          open && 'bg-raised text-foreground',
        )}
      >
        <span className="truncate">{current?.label ?? label}</span>
        <ChevronDown className="text-fg-3 size-3 shrink-0" aria-hidden />
      </button>

      {open ? (
        <div
          id={listId}
          ref={list}
          role="listbox"
          aria-label={label}
          className={cn(
            'bg-pop dy-scroll dy-pop-in absolute top-[calc(100%+4px)] z-40 max-h-[18rem] min-w-[11rem] overflow-y-auto',
            'rounded-lg border p-1 shadow-pop',
            align === 'end' ? 'right-0' : 'left-0',
          )}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              setOpen(false);
              wrap.current?.querySelector('button')?.focus();
              return;
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              move(event.target as HTMLElement, 1);
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              move(event.target as HTMLElement, -1);
            }
          }}
        >
          {options.map((option) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                data-selected={selected}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm outline-none',
                  'hover:bg-raised focus-visible:bg-raised',
                  selected ? 'text-foreground' : 'text-fg-2',
                )}
              >
                <Check
                  className={cn('size-3 shrink-0', selected ? 'text-gate' : 'opacity-0')}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.hint ? (
                  <span className="text-fg-3 tnum text-2xs">{option.hint}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
